import AccessTimeIcon from '@mui/icons-material/AccessTime';
import LoyaltyIcon from '@mui/icons-material/Loyalty';
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined';
import StarRoundedIcon from '@mui/icons-material/StarRounded';
import {
    Box,
    Button,
    Card,
    CardContent,
    Container,
    Grid2 as Grid,
    Skeleton,
    Typography,
    useTheme,
} from '@mui/material';
import moment from 'moment';
import React, { useContext, useMemo } from 'react';
import { MdLocalFireDepartment } from 'react-icons/md';
import { Link } from 'react-router-dom';
import { productURL } from '../../utils/functions/common';
import {
    categoryLabel,
    formatProductDuration,
    gridCardRating,
    promoBadgeLabel,
} from '../../utils/functions/productCardMeta';
import { Product, ProductCategories } from '../../utils/models/products';
import { ProductsStore } from '../../utils/stores/ProductsStore';

export interface ProductGridProps {
    readonly backgroundColor?: boolean;
    readonly tag?: string;
    readonly category?: ProductCategories;
}

/** xs/sm: 1 per row; md: 2 per row (or full width if only one product); lg+: 2 cols for 2 or 4 items, 3 cols otherwise. */
function gridItemSize(productCount: number): { xs: number; sm: number; md: number; lg: number } {
    if (productCount === 1) {
        return { xs: 12, sm: 12, md: 12, lg: 12 };
    }
    const lg = productCount === 2 || productCount === 4 ? 6 : 4;
    return { xs: 12, sm: 12, md: 6, lg };
}

export default function ProductDisplayGrid({ backgroundColor, tag, category }: ProductGridProps) {
    const theme = useTheme();
    const productStore = useContext(ProductsStore);

    // Derived purely from the catalogue — memoized so availability fetches (which change
    // the store value) don't recompute this list or churn the product cards/imagery.
    const productsToDisplay = useMemo(
        () =>
            tag
                ? category
                    ? productStore.products
                          .filter((prod) => prod.tags?.includes(tag))
                          .filter((prod2) => prod2.category === category)
                    : productStore.products.filter((prod) => prod.tags?.includes(tag))
                : productStore.products.filter((prod) => prod.category === category),
        [productStore.products, tag, category]
    );

    const productCount = productsToDisplay.length;
    const itemGridSize = gridItemSize(productStore.isLoading ? Math.max(productCount, 3) : Math.max(productCount, 1));

    const isOnSale = (code: string) => {
        const product = productStore.products.filter((prod) => prod.productCode === code)[0];
        const sale = product.sales?.filter((s) => moment().isAfter(s.startDate) && moment().isBefore(s.endDate));
        const salePrice = sale && sale.length !== 0 ? sale[0].fareTypes[0].price : null;
        return salePrice;
    };

    return (
        <Box
            sx={{
                pt: 2.5,
                pb: 2.5,
                ...(backgroundColor && { bgcolor: 'background.subtle' }),
            }}
        >
            <Container maxWidth="lg">
                <Grid
                    container
                    spacing={{ xs: 2, sm: 2.5 }}
                    sx={{
                        py: 1.25,
                        justifyContent: 'flex-start',
                        width: '100%',
                        margin: 'auto',
                        [theme.breakpoints.only('md')]: { width: '90%' },
                        [theme.breakpoints.only('xs')]: { px: 0 },
                    }}
                >
                    {productStore.isLoading
                        ? Array.from(Array(3).keys()).map((n) => (
                              <Grid key={`skel${n}`} size={itemGridSize}>
                                  <Skeleton variant="rounded" height={480} sx={{ borderRadius: 2, width: '100%' }} />
                              </Grid>
                          ))
                        : productsToDisplay.map((product: Product) => {
                              const linkURL = productURL(product);
                              const salePrice = isOnSale(product.productCode);
                              const badge = promoBadgeLabel(product);
                              const durationText = formatProductDuration(product);
                              const location = categoryLabel(product.category);

                              return (
                                  <Grid key={product.productCode} size={itemGridSize}>
                                      <Card
                                          elevation={0}
                                          sx={{
                                              height: '100%',
                                              display: 'flex',
                                              flexDirection: 'column',
                                              overflow: 'hidden',
                                              borderRadius: 2,
                                              bgcolor: 'background.paper',
                                              boxShadow: '0 4px 24px rgba(0,0,0,0.08)',
                                              border: '1px solid',
                                              borderColor: 'rgba(0,0,0,0.06)',
                                          }}
                                      >
                                          <Box sx={{ position: 'relative', flexShrink: 0 }}>
                                              <Link to={linkURL} style={{ display: 'block', lineHeight: 0 }}>
                                                  <Box
                                                      component="img"
                                                      src={product?.photos?.[0]?.mediumURL}
                                                      alt={product?.photos?.[0]?.alt || product.name}
                                                      sx={{
                                                          width: '100%',
                                                          height: { xs: 220, sm: 240 },
                                                          objectFit: 'cover',
                                                          display: 'block',
                                                          transition: 'opacity 0.2s ease',
                                                          '&:hover': { opacity: 0.92 },
                                                      }}
                                                  />
                                              </Link>
                                              {badge && !(product.saleTips && product.saleTips.length > 0) ? (
                                                  <Typography
                                                      component="span"
                                                      sx={{
                                                          position: 'absolute',
                                                          top: 12,
                                                          left: 12,
                                                          px: 1.25,
                                                          py: 0.5,
                                                          borderRadius: 1,
                                                          fontSize: '0.6875rem',
                                                          fontWeight: 700,
                                                          letterSpacing: '0.06em',
                                                          color: 'primary.contrastText',
                                                          bgcolor: 'primary.main',
                                                          lineHeight: 1.2,
                                                      }}
                                                  >
                                                      {badge}
                                                  </Typography>
                                              ) : null}
                                              {durationText ? (
                                                  <Box
                                                      sx={{
                                                          position: 'absolute',
                                                          top: 12,
                                                          right: 12,
                                                          display: 'inline-flex',
                                                          alignItems: 'center',
                                                          gap: 0.5,
                                                          px: 1.25,
                                                          py: 0.5,
                                                          borderRadius: 10,
                                                          bgcolor: 'rgba(0,0,0,0.55)',
                                                          color: 'common.white',
                                                          backdropFilter: 'blur(4px)',
                                                      }}
                                                  >
                                                      <AccessTimeIcon sx={{ fontSize: 16 }} />
                                                      <Typography
                                                          component="span"
                                                          sx={{
                                                              fontSize: '0.75rem',
                                                              fontWeight: 600,
                                                              lineHeight: 1,
                                                          }}
                                                      >
                                                          {durationText}
                                                      </Typography>
                                                  </Box>
                                              ) : null}
                                          </Box>

                                          <CardContent
                                              sx={{
                                                  flex: 1,
                                                  display: 'flex',
                                                  flexDirection: 'column',
                                                  p: 2.5,
                                                  '&:last-child': { pb: 2.5 },
                                              }}
                                          >
                                              <Box
                                                  sx={{
                                                      display: 'flex',
                                                      alignItems: 'center',
                                                      flexWrap: 'wrap',
                                                      gap: 0.75,
                                                      mb: 1.5,
                                                      color: 'text.secondary',
                                                      typography: 'caption',
                                                  }}
                                              >
                                                  <Box
                                                      sx={{
                                                          display: 'flex',
                                                          alignItems: 'center',
                                                          gap: 0.35,
                                                      }}
                                                  >
                                                      <StarRoundedIcon sx={{ fontSize: 18, color: 'primary.main' }} />
                                                      <Typography
                                                          component="span"
                                                          variant="caption"
                                                          sx={{ fontWeight: 600, color: 'text.primary' }}
                                                      >
                                                          {gridCardRating(product.productCode)}
                                                      </Typography>
                                                  </Box>
                                                  <Box
                                                      component="span"
                                                      sx={{
                                                          width: 3,
                                                          height: 3,
                                                          borderRadius: '50%',
                                                          bgcolor: 'text.hint',
                                                          display: 'inline-block',
                                                      }}
                                                  />
                                                  <Box
                                                      sx={{
                                                          display: 'flex',
                                                          alignItems: 'center',
                                                          gap: 0.35,
                                                      }}
                                                  >
                                                      <PlaceOutlinedIcon sx={{ fontSize: 16, opacity: 0.75 }} />
                                                      <Typography component="span" variant="caption">
                                                          {location}
                                                      </Typography>
                                                  </Box>
                                              </Box>

                                              <Typography
                                                  component={Link}
                                                  to={linkURL}
                                                  variant="h6"
                                                  sx={{
                                                      fontFamily: 'Georgia, "Times New Roman", "Source Serif 4", serif',
                                                      fontWeight: 700,
                                                      fontSize: '1.125rem',
                                                      lineHeight: 1.35,
                                                      color: 'text.primary',
                                                      textDecoration: 'none',
                                                      mb: 1,
                                                      '&:hover': { color: 'primary.main' },
                                                  }}
                                              >
                                                  {product.name}
                                              </Typography>

                                              <Typography
                                                  variant="body2"
                                                  sx={{
                                                      color: 'text.secondary',
                                                      lineHeight: 1.55,
                                                      fontSize: '0.875rem',
                                                      flex: 1,
                                                      mb: 1.5,
                                                  }}
                                              >
                                                  {product.shortDescription}
                                              </Typography>

                                              {(salePrice || (product.saleTips && product.saleTips.length > 0)) && (
                                                  <Box
                                                      sx={{
                                                          display: 'flex',
                                                          flexWrap: 'wrap',
                                                          gap: 1,
                                                          mb: 2,
                                                      }}
                                                  >
                                                      {salePrice ? (
                                                          <Box
                                                              sx={{
                                                                  display: 'inline-flex',
                                                                  alignItems: 'center',
                                                                  px: 1,
                                                                  py: 0.5,
                                                                  borderRadius: 1.25,
                                                                  bgcolor: 'primary.light',
                                                                  color: 'primary.dark',
                                                                  fontSize: '0.8125rem',
                                                                  fontWeight: 600,
                                                              }}
                                                          >
                                                              <LoyaltyIcon sx={{ fontSize: 18, mr: 0.5 }} />
                                                              On sale
                                                          </Box>
                                                      ) : null}
                                                      {product.saleTips?.map((tip) => (
                                                          <Box
                                                              key={tip}
                                                              sx={{
                                                                  display: 'inline-flex',
                                                                  alignItems: 'center',
                                                                  px: 1,
                                                                  py: 0.5,
                                                                  borderRadius: 1.25,
                                                                  bgcolor: 'primary.light',
                                                                  color: 'primary.dark',
                                                                  fontSize: '0.8125rem',
                                                                  fontWeight: 600,
                                                              }}
                                                          >
                                                              <MdLocalFireDepartment
                                                                  style={{ marginRight: 6, fontSize: 16 }}
                                                              />
                                                              {tip}
                                                          </Box>
                                                      ))}
                                                  </Box>
                                              )}

                                              <Box
                                                  sx={{
                                                      display: 'flex',
                                                      justifyContent: 'space-between',
                                                      alignItems: 'flex-end',
                                                      gap: 2,
                                                      mt: 'auto',
                                                      pt: 1,
                                                  }}
                                              >
                                                  <Box>
                                                      <Typography
                                                          variant="caption"
                                                          sx={{
                                                              display: 'block',
                                                              color: 'text.hint',
                                                              textTransform: 'lowercase',
                                                              fontSize: '0.7rem',
                                                              mb: 0.25,
                                                          }}
                                                      >
                                                          from
                                                      </Typography>
                                                      <Box
                                                          sx={{
                                                              display: 'flex',
                                                              alignItems: 'baseline',
                                                              flexWrap: 'wrap',
                                                              gap: 0.5,
                                                          }}
                                                      >
                                                          {salePrice ? (
                                                              <>
                                                                  <Typography
                                                                      component="span"
                                                                      sx={{
                                                                          fontSize: '1.1rem',
                                                                          textDecoration: 'line-through',
                                                                          color: 'text.secondary',
                                                                          fontWeight: 500,
                                                                      }}
                                                                  >
                                                                      ${product?.displayPrice?.price}
                                                                  </Typography>
                                                                  <Typography
                                                                      component="span"
                                                                      sx={{
                                                                          fontSize: '1.5rem',
                                                                          fontWeight: 700,
                                                                          color: 'text.primary',
                                                                      }}
                                                                  >
                                                                      ${salePrice}
                                                                  </Typography>
                                                              </>
                                                          ) : (
                                                              <Typography
                                                                  component="span"
                                                                  sx={{
                                                                      fontSize: '1.5rem',
                                                                      fontWeight: 700,
                                                                      color: 'text.primary',
                                                                  }}
                                                              >
                                                                  ${product?.displayPrice?.price}
                                                              </Typography>
                                                          )}
                                                          <Typography
                                                              component="span"
                                                              variant="caption"
                                                              sx={{ color: 'text.hint', ml: 0.25 }}
                                                          >
                                                              pp
                                                          </Typography>
                                                      </Box>
                                                  </Box>
                                                  <Button
                                                      component={Link}
                                                      to={linkURL}
                                                      variant="contained"
                                                      color="primary"
                                                      disableElevation
                                                      sx={{
                                                          flexShrink: 0,
                                                          px: 2,
                                                          py: 1,
                                                          minWidth: 120,
                                                          fontWeight: 700,
                                                          fontSize: '0.75rem',
                                                          letterSpacing: '0.06em',
                                                          borderRadius: 1,
                                                          boxShadow: 'none',
                                                      }}
                                                  >
                                                      BOOK NOW
                                                  </Button>
                                              </Box>
                                          </CardContent>
                                      </Card>
                                  </Grid>
                              );
                          })}
                </Grid>
            </Container>
        </Box>
    );
}
